/**
 * Error utilities for parsing and handling API errors
 */

export interface ParsedError {
  code?: string;
  message: string;
  details?: any;
  shouldShowCTA: boolean;
  originalError?: any;
}

/**
 * Parse API error responses from various formats
 * Handles both JSON error objects and text error messages
 */
export function parseAPIError(error: any): ParsedError {
  // Handle null/undefined
  if (!error) {
    return {
      message: 'An unexpected error occurred',
      shouldShowCTA: false,
      originalError: error,
    };
  }

  // Handle Error instances
  if (error instanceof Error) {
    // Check if it's a parsed JSON error in the message
    try {
      const parsed = JSON.parse(error.message);
      return parseAPIError(parsed);
    } catch {
      // Not JSON, treat as regular error
      const message = error.message || 'An unexpected error occurred';
      const shouldShowCTA = checkIfQuotaError(message);

      return {
        message,
        shouldShowCTA,
        originalError: error,
      };
    }
  }

  // Handle API response error objects
  if (typeof error === 'object') {
    // Check for standard API error structure
    if (error.error && typeof error.error === 'object') {
      return {
        code: error.error.code,
        message: error.error.message || error.message || 'An error occurred',
        details: error.error.details,
        shouldShowCTA: error.cta === true || error.error.code === 'QUOTA_EXCEEDED',
        originalError: error,
      };
    }

    // Check for direct error properties
    if (error.code || error.message) {
      return {
        code: error.code,
        message: error.message || 'An error occurred',
        details: error.details,
        shouldShowCTA: error.cta === true || error.code === 'QUOTA_EXCEEDED',
        originalError: error,
      };
    }

    // Try to extract message from object
    const message = error.message || error.error || JSON.stringify(error);
    return {
      message,
      shouldShowCTA: checkIfQuotaError(message),
      originalError: error,
    };
  }

  // Handle string errors
  if (typeof error === 'string') {
    // Try to parse as JSON first
    try {
      const parsed = JSON.parse(error);
      return parseAPIError(parsed);
    } catch {
      // Not JSON, use as message
      return {
        message: error,
        shouldShowCTA: checkIfQuotaError(error),
        originalError: error,
      };
    }
  }

  // Fallback for unknown error types
  return {
    message: String(error) || 'An unexpected error occurred',
    shouldShowCTA: false,
    originalError: error,
  };
}

/**
 * Parse streaming error responses that may have prefix format
 * e.g., "0: {json}" or just "{json}"
 */
export function parseStreamError(errorText: string): ParsedError {
  if (!errorText) {
    return {
      message: 'Stream error occurred',
      shouldShowCTA: false,
    };
  }

  // Check for streaming prefix format "0: {json}"
  const streamPrefixMatch = errorText.match(/^\d+:\s*(.+)$/);
  const jsonContent = streamPrefixMatch ? streamPrefixMatch[1] : errorText;

  try {
    const parsed = JSON.parse(jsonContent);
    return parseAPIError(parsed);
  } catch {
    // Not JSON, treat as plain text error
    return {
      message: errorText,
      shouldShowCTA: checkIfQuotaError(errorText),
    };
  }
}

/**
 * Check if an error message indicates a quota exceeded error
 */
function checkIfQuotaError(message: string): boolean {
  if (!message || typeof message !== 'string') return false;

  const lowerMessage = message.toLowerCase();
  return (
    lowerMessage.includes('quota') ||
    lowerMessage.includes('limit exceeded') ||
    lowerMessage.includes('daily limit') ||
    lowerMessage.includes('usage limit') ||
    lowerMessage.includes('rate limit')
  );
}

/**
 * Get user-friendly error message based on error code
 */
export function getUserFriendlyErrorMessage(error: ParsedError): string {
  switch (error.code) {
    case 'QUOTA_EXCEEDED':
      return 'You have reached your daily usage limit. Please upgrade to continue.';
    case 'MISSING_USER_ID':
      return 'User identification is required. Please refresh the page.';
    case 'INVALID_INPUT':
      return 'The provided input is invalid. Please check and try again.';
    case 'AI_SERVICE_ERROR':
      return 'The AI service is temporarily unavailable. Please try again.';
    case 'PROCESSING_ERROR':
      return 'An error occurred while processing your request. Please try again.';
    case 'NETWORK_ERROR':
      return 'Connection error. Please check your internet and try again.';
    case 'TIMEOUT_ERROR':
      return 'The request took too long. Please try again.';
    default:
      return error.message || 'An unexpected error occurred. Please try again.';
  }
}

/**
 * Determine the CTA dialog type based on error
 */
export function getCTADialogType(error: ParsedError): string | null {
  if (!error.shouldShowCTA) return null;

  switch (error.code) {
    case 'QUOTA_EXCEEDED':
      return 'quota_exceeded';
    case 'MISSING_USER_ID':
      return 'sign_up_required';
    default:
      // Check message for clues
      const message = (error.message || '').toLowerCase();
      if (message.includes('premium') || message.includes('upgrade')) {
        return 'premium_features';
      }
      if (message.includes('sign up') || message.includes('login')) {
        return 'sign_up_required';
      }
      return 'quota_exceeded'; // Default CTA type
  }
}
